<!-- views/CourseView.vue - Fixed version -->
<template>
  <div class="course-view">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading course...</p>
    </div>

    <div v-else-if="course" class="course-container">
      <!-- Course Header -->
      <div
        class="course-header"
        :style="{
          backgroundImage: `linear-gradient(135deg, ${primaryBlue}, ${primaryBlue}dd), url(${course.thumbnail || defaultThumbnail})`,
        }"
      >
        <div class="header-content">
          <div class="breadcrumb">
            <router-link to="/">Home</router-link>
            <span>/</span>
            <router-link to="/explore">Explore</router-link>
            <span>/</span>
            <span class="current">{{ course.category }}</span>
          </div>

          <h1>{{ course.title }}</h1>
          <p class="course-description">{{ course.description }}</p>

          <div class="course-meta">
            <div class="meta-item">
              <span class="icon">👤</span>
              <span>By {{ course.creatorName }}</span>
            </div>
            <div class="meta-item">
              <span class="icon">⭐</span>
              <span
                >{{ course.rating?.toFixed(1) || "New" }} ({{
                  course.ratingsCount || 0
                }}
                reviews)</span
              >
            </div>
            <div class="meta-item">
              <span class="icon">🎬</span>
              <span>{{ totalVideos }} videos</span>
            </div>
            <div class="meta-item">
              <span class="icon">⏱️</span>
              <span>{{ totalDuration }}</span>
            </div>
            <div class="meta-item">
              <span class="icon">👨‍🎓</span>
              <span>{{ course.totalStudents || 0 }} students</span>
            </div>
          </div>

          <div class="course-actions">
            <div class="price-tag">
              <span v-if="course.price > 0" class="price">
                ${{ course.price.toFixed(2) }}
              </span>
              <span v-else class="free-badge">Free</span>
            </div>

            <button
              class="btn-enroll"
              @click="handleEnroll"
              :disabled="isEnrolling"
            >
              <span v-if="isEnrolling" class="spinner-small"></span>
              <span v-else>
                {{
                  isEnrolled
                    ? "Continue Learning"
                    : course.price > 0
                      ? "Enroll Now"
                      : "Start Learning"
                }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Course Body -->
      <div class="course-body">
        <div class="main-content">
          <!-- Course Player / Content -->
          <div class="content-area">
            <div
              v-if="isEnrolled || course.price === 0 || isCreator"
              class="player-wrapper"
            >
              <!-- Loading state for module content -->
              <div v-if="loadingModule" class="module-loading">
                <div class="spinner"></div>
                <p>Loading module content...</p>
              </div>

              <div v-else-if="currentModule" class="module-player">
                <!-- Video Player -->
                <div v-if="currentModule.type === 'video'" class="video-player">
                  <!-- YouTube Video -->
                  <div
                    v-if="currentModule.content?.source === 'youtube'"
                    class="youtube-player"
                  >
                    <iframe
                      :src="
                        currentModule.content.embed_url ||
                        getYouTubeEmbedUrl(currentModule.content.embed_url)
                      "
                      frameborder="0"
                      allowfullscreen
                      allow="
                        accelerometer;
                        autoplay;
                        clipboard-write;
                        encrypted-media;
                        gyroscope;
                        picture-in-picture;
                      "
                      style="
                        width: 100%;
                        aspect-ratio: 16/9;
                        border-radius: 8px;
                      "
                    ></iframe>
                    <div class="video-controls-bottom">
                      <span>{{ currentModule.title }}</span>
                      <span class="video-duration">{{
                        currentModule.content?.duration || "YouTube"
                      }}</span>
                    </div>
                  </div>

                  <!-- Uploaded Video -->
                  <div v-else>
                    <video
                      ref="videoPlayer"
                      controls
                      class="video-element"
                      @loadedmetadata="onVideoLoaded"
                      @timeupdate="onVideoProgress"
                    >
                      <source
                        :src="currentModule.content?.url"
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                    <div class="video-controls-bottom">
                      <span>{{ currentModule.title }}</span>
                      <span class="video-duration">{{
                        currentModule.content?.duration || "0:00"
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Article Viewer -->
                <div
                  v-else-if="currentModule.type === 'article'"
                  class="article-viewer"
                >
                  <div class="article-header">
                    <h2>{{ currentModule.title }}</h2>
                    <span
                      v-if="currentModule.isPublished"
                      class="published-badge"
                      >✅ Published</span
                    >
                    <span v-else class="draft-badge">📝 Draft</span>
                  </div>
                  <div
                    class="article-body"
                    v-html="currentModule.content?.html || 'No content yet'"
                  ></div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-content">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <path
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  <h3>Select a module to begin</h3>
                  <p>Choose a module from the sidebar to start learning</p>
                </div>
              </div>

              <!-- Module Navigation -->
              <div class="module-navigation">
                <button
                  v-if="hasPreviousModule"
                  @click="navigateModule(-1)"
                  class="nav-btn prev"
                >
                  ← Previous
                </button>
                <button
                  v-if="hasNextModule"
                  @click="navigateModule(1)"
                  class="nav-btn next"
                >
                  Next →
                </button>
              </div>
            </div>

            <!-- Enrollment Required -->
            <div v-else class="enrollment-required">
              <div class="enrollment-card">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <path
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                <h3>Enroll to Access Content</h3>
                <p>
                  This course requires enrollment. Click the button above to get
                  started.
                </p>
                <div class="preview-info">
                  <h4>What you'll learn:</h4>
                  <ul>
                    <li
                      v-for="(section, index) in course.sections?.slice(0, 3)"
                      :key="section.id"
                    >
                      {{ index + 1 }}. {{ section.title }}
                    </li>
                  </ul>
                  <p v-if="course.sections?.length > 3" class="more-sections">
                    +{{ course.sections.length - 3 }} more sections
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Course Sidebar -->
          <div class="course-sidebar">
            <div class="sidebar-card">
              <h3>Course Content</h3>
              <div class="sections-list">
                <div
                  v-for="(section, sectionIndex) in course.sections"
                  :key="section.id"
                  class="section-group"
                >
                  <div
                    class="section-header"
                    @click="toggleSection(section.id)"
                  >
                    <span class="section-title">
                      <span class="section-number"
                        >{{ sectionIndex + 1 }}.</span
                      >
                      {{ section.title }}
                    </span>
                    <span class="section-count"
                      >{{ section.modules?.length || 0 }} modules</span
                    >
                  </div>

                  <div
                    v-if="expandedSections.includes(section.id)"
                    class="modules-list"
                  >
                    <div
                      v-for="(module, moduleIndex) in section.modules"
                      :key="module.id"
                      class="module-item"
                      :class="{
                        active: currentModule?.id === module.id,
                        completed: isModuleCompleted(module.id),
                        locked: !isEnrolled && course.price > 0 && !isCreator,
                        'module-loading':
                          loadingModule && currentModule?.id === module.id,
                      }"
                      @click="selectModule(module, section.id)"
                    >
                      <span class="module-status">
                        <span
                          v-if="isModuleCompleted(module.id)"
                          class="status-completed"
                          >✓</span
                        >
                        <span
                          v-else-if="
                            !isEnrolled && course.price > 0 && !isCreator
                          "
                          class="status-locked"
                          >🔒</span
                        >
                        <span
                          v-else-if="
                            loadingModule && currentModule?.id === module.id
                          "
                          class="status-loading"
                          >⏳</span
                        >
                        <span v-else class="status-number">{{
                          moduleIndex + 1
                        }}</span>
                      </span>
                      <span class="module-title">{{ module.title }}</span>
                      <span class="module-type">{{
                        module.type === "video" ? "🎬" : "📄"
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Creator Info -->
            <div class="sidebar-card creator-card">
              <h3>About the Creator</h3>
              <div class="creator-info">
                <div class="creator-avatar">
                  <img
                    :src="
                      course.creatorAvatar ||
                      `https://ui-avatars.com/api/?name=${course.creatorName}&background=FF8000&color=fff`
                    "
                    :alt="course.creatorName"
                  />
                </div>
                <div class="creator-details">
                  <h4>{{ course.creatorName }}</h4>
                  <p>{{ course.creatorBio || "Course Creator" }}</p>
                </div>
              </div>
              <div
                class="creator-social"
                v-if="
                  course.socialLinks &&
                  Object.keys(course.socialLinks).length > 0
                "
              >
                <a
                  v-for="(url, platform) in course.socialLinks"
                  :key="platform"
                  :href="url"
                  target="_blank"
                  class="social-link"
                >
                  {{ getSocialIcon(platform) }}
                </a>
              </div>
            </div>

            <!-- Course Stats -->
            <div class="sidebar-card stats-card">
              <h3>Course Stats</h3>
              <div class="stats-list">
                <div class="stat-item">
                  <span class="stat-label">Total Videos</span>
                  <span class="stat-value">{{ totalVideos }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Total Articles</span>
                  <span class="stat-value">{{ totalArticles }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Total Hours</span>
                  <span class="stat-value">{{ totalDuration }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Students</span>
                  <span class="stat-value">{{
                    course.totalStudents || 0
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Reviews Section -->
        <div class="reviews-section" id="reviews">
          <div class="reviews-header">
            <h2>Reviews</h2>
            <div class="reviews-summary">
              <span class="average-rating"
                >⭐ {{ course.rating?.toFixed(1) || "New" }}</span
              >
              <span class="review-count"
                >{{ course.ratingsCount || 0 }} reviews</span
              >
            </div>
          </div>

          <!-- Write Review -->
          <div v-if="isEnrolled" class="write-review">
            <h4>Write a Review</h4>
            <div class="rating-input">
              <button
                v-for="star in 5"
                :key="star"
                class="star-btn"
                :class="{ active: star <= reviewRating }"
                @click="reviewRating = star"
              >
                ⭐
              </button>
            </div>
            <textarea
              v-model="reviewText"
              placeholder="Share your experience with this course..."
              rows="3"
            ></textarea>
            <button
              @click="submitReview"
              :disabled="!reviewText || reviewRating === 0 || submittingReview"
              class="btn-primary"
            >
              {{ submittingReview ? "Submitting..." : "Submit Review" }}
            </button>
          </div>

          <!-- Reviews List -->
          <div class="reviews-list">
            <div v-if="course.reviews?.length === 0" class="no-reviews">
              <p>No reviews yet. Be the first to review this course!</p>
            </div>
            <div
              v-for="review in course.reviews?.slice().reverse()"
              :key="review.id"
              class="review-item"
            >
              <div class="review-header">
                <div class="reviewer-info">
                  <img
                    :src="
                      review.userAvatar ||
                      `https://ui-avatars.com/api/?name=${review.userName}&background=FF8000&color=fff`
                    "
                    :alt="review.userName"
                  />
                  <div>
                    <span class="reviewer-name">{{ review.userName }}</span>
                    <span class="review-date">{{
                      formatDate(review.createdAt)
                    }}</span>
                  </div>
                </div>
                <div class="review-rating">
                  {{ "⭐".repeat(review.rating) }}
                </div>
              </div>
              <p class="review-text">{{ review.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="error-state">
      <svg
        width="80"
        height="80"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <h2>Course Not Found</h2>
      <p>The course you're looking for doesn't exist or has been removed.</p>
      <router-link to="/" class="btn-primary">Go Home</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useCourseStore } from "@/stores/course";
import api from "@/services/api";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const courseStore = useCourseStore();

// State
const course = ref(null);
const loading = ref(true);
const loadingModule = ref(false);
const isEnrolled = ref(false);
const isEnrolling = ref(false);
const currentModule = ref(null);
const expandedSections = ref([]);
const videoPlayer = ref(null);
const isCreator = ref(false);

// Review state
const reviewRating = ref(0);
const reviewText = ref("");
const submittingReview = ref(false);

// Colors
const primaryBlue = "#001829";
const defaultThumbnail =
  "https://via.placeholder.com/1280x720/001829/FF8000?text=MyHarf+Course";

// YouTube helper
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const videoId = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/,
  )?.[1];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

// Computed
const totalVideos = computed(() => {
  let count = 0;
  course.value?.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (module.type === "video") count++;
    });
  });
  return count;
});

const totalArticles = computed(() => {
  let count = 0;
  course.value?.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (module.type === "article") count++;
    });
  });
  return count;
});

const totalDuration = computed(() => {
  let totalSeconds = 0;
  course.value?.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      if (module.type === "video" && module.content?.duration) {
        const parts = module.content.duration.split(":");
        totalSeconds += parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
    });
  });

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
});

const hasPreviousModule = computed(() => {
  if (!course.value || !currentModule.value) return false;
  const allModules = getAllModules();
  const currentIndex = allModules.findIndex(
    (m) => m.id === currentModule.value.id,
  );
  return currentIndex > 0;
});

const hasNextModule = computed(() => {
  if (!course.value || !currentModule.value) return false;
  const allModules = getAllModules();
  const currentIndex = allModules.findIndex(
    (m) => m.id === currentModule.value.id,
  );
  return currentIndex < allModules.length - 1;
});

// Methods
const getAllModules = () => {
  const modules = [];
  course.value?.sections?.forEach((section) => {
    section.modules?.forEach((module) => {
      modules.push({ ...module, sectionId: section.id });
    });
  });
  return modules;
};

const toggleSection = (sectionId) => {
  const index = expandedSections.value.indexOf(sectionId);
  if (index > -1) {
    expandedSections.value.splice(index, 1);
  } else {
    expandedSections.value.push(sectionId);
  }
};

const fetchModuleContent = async (moduleId, sectionId) => {
  loadingModule.value = true;
  try {
    const response = await api.get(
      `/courses/${course.value.id}/sections/${sectionId}/modules/${moduleId}`,
    );
    if (response.data.success) {
      const moduleData = response.data.data;

      // Update the module in the course data
      const sectionIndex = course.value.sections.findIndex(
        (s) => s.id === sectionId,
      );
      if (sectionIndex !== -1) {
        const moduleIndex = course.value.sections[
          sectionIndex
        ].modules.findIndex((m) => m.id === moduleId);
        if (moduleIndex !== -1) {
          course.value.sections[sectionIndex].modules[moduleIndex] = {
            ...course.value.sections[sectionIndex].modules[moduleIndex],
            ...moduleData,
          };
        }
      }

      currentModule.value = moduleData;
      return moduleData;
    } else {
      console.error("Failed to fetch module:", response.data.error);
      // Fallback to existing module data
      const sectionIndex = course.value.sections.findIndex(
        (s) => s.id === sectionId,
      );
      if (sectionIndex !== -1) {
        const moduleIndex = course.value.sections[
          sectionIndex
        ].modules.findIndex((m) => m.id === moduleId);
        if (moduleIndex !== -1) {
          const existingModule =
            course.value.sections[sectionIndex].modules[moduleIndex];
          if (existingModule) {
            currentModule.value = existingModule;
            return existingModule;
          }
        }
      }
      return null;
    }
  } catch (error) {
    console.error("Error fetching module:", error);
    // Fallback to existing module data
    const sectionIndex = course.value.sections.findIndex(
      (s) => s.id === sectionId,
    );
    if (sectionIndex !== -1) {
      const moduleIndex = course.value.sections[sectionIndex].modules.findIndex(
        (m) => m.id === moduleId,
      );
      if (moduleIndex !== -1) {
        const existingModule =
          course.value.sections[sectionIndex].modules[moduleIndex];
        if (existingModule) {
          currentModule.value = existingModule;
          return existingModule;
        }
      }
    }
    return null;
  } finally {
    loadingModule.value = false;
  }
};

const selectModule = async (module, sectionId) => {
  // If it's the same module, don't reload
  if (currentModule.value?.id === module.id && !loadingModule.value) {
    return;
  }

  // Check if we have the full module content already
  const sectionIndex = course.value.sections.findIndex(
    (s) => s.id === sectionId,
  );
  if (sectionIndex !== -1) {
    const moduleIndex = course.value.sections[sectionIndex].modules.findIndex(
      (m) => m.id === module.id,
    );
    if (moduleIndex !== -1) {
      const existingModule =
        course.value.sections[sectionIndex].modules[moduleIndex];
      // If the module already has content loaded, use it
      if (
        existingModule.content &&
        Object.keys(existingModule.content).length > 0
      ) {
        currentModule.value = existingModule;
        return;
      }
    }
  }

  // Otherwise fetch from API
  await fetchModuleContent(module.id, sectionId);
};

const navigateModule = async (direction) => {
  const allModules = getAllModules();
  const currentIndex = allModules.findIndex(
    (m) => m.id === currentModule.value.id,
  );
  const newIndex = currentIndex + direction;
  if (newIndex >= 0 && newIndex < allModules.length) {
    const nextModule = allModules[newIndex];
    await selectModule(nextModule, nextModule.sectionId);
  }
};

const isModuleCompleted = (moduleId) => {
  // TODO: Track progress in backend
  return false;
};

const handleEnroll = async () => {
  if (isEnrolled.value) {
    const player = document.querySelector(".player-wrapper");
    if (player) {
      player.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    return;
  }

  if (course.value.price > 0) {
    router.push(`/checkout/${course.value.id}`);
  } else {
    isEnrolling.value = true;
    try {
      // TODO: Add enrollment to backend
      isEnrolled.value = true;
      const firstModule = getAllModules()[0];
      if (firstModule) {
        await selectModule(firstModule, firstModule.sectionId);
      }
    } catch (error) {
      console.error("Enrollment error:", error);
      alert("Failed to enroll. Please try again.");
    } finally {
      isEnrolling.value = false;
    }
  }
};

// Video playback tracking
const onVideoLoaded = () => {
  console.log("Video loaded");
};

const onVideoProgress = () => {
  // Track progress for completion
};

// Reviews
const submitReview = async () => {
  if (!authStore.isAuthenticated) {
    alert("Please sign in to leave a review.");
    return;
  }

  submittingReview.value = true;
  try {
    const result = await courseStore.addReview(course.value.id, {
      userId: authStore.user.uid,
      userName: authStore.user.displayName || authStore.user.email,
      userAvatar: authStore.user.photoURL || null,
      rating: reviewRating.value,
      text: reviewText.value,
    });

    if (result.success) {
      if (course.value.reviews) {
        course.value.reviews.push(result.review);
      } else {
        course.value.reviews = [result.review];
      }
      course.value.rating =
        (course.value.rating || 0) +
        (reviewRating.value - (course.value.rating || 0)) /
          (course.value.ratingsCount || 0);
      course.value.ratingsCount = (course.value.ratingsCount || 0) + 1;

      reviewRating.value = 0;
      reviewText.value = "";
      alert("Review submitted successfully!");
    }
  } catch (error) {
    console.error("Review submission error:", error);
    alert("Failed to submit review. Please try again.");
  } finally {
    submittingReview.value = false;
  }
};

const getSocialIcon = (platform) => {
  const icons = {
    github: "🐙",
    website: "🌐",
    linkedin: "💼",
    twitter: "🐦",
    youtube: "▶️",
    instagram: "📸",
  };
  return icons[platform] || "🔗";
};

const formatDate = (timestamp) => {
  if (!timestamp) return "Recently";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Load course
onMounted(async () => {
  const courseId = route.params.id;
  if (!courseId) {
    loading.value = false;
    return;
  }

  // Check if user is authenticated
  if (authStore.isAuthenticated) {
    // Try to get the course for editing (draft access)
    const editResult = await courseStore.fetchCourseForEdit(courseId);
    if (editResult.success) {
      course.value = editResult.course;
      isCreator.value = true;
      isEnrolled.value = true; // Creator can see all content

      // Auto-expand sections and select first module
      if (course.value.sections?.length > 0) {
        expandedSections.value.push(course.value.sections[0].id);
        const firstModule = getAllModules()[0];
        if (firstModule) {
          await selectModule(firstModule, firstModule.sectionId);
        }
      }
      loading.value = false;
      return;
    }
  }

  // Fallback to public course view
  const result = await courseStore.fetchPublishedCourse(courseId);

  if (result.success) {
    course.value = result.course;

    // Check if user is enrolled
    if (authStore.isAuthenticated && course.value.isEnrolled) {
      isEnrolled.value = true;
    }

    // Auto-expand first section and select first module
    if (course.value.sections?.length > 0) {
      expandedSections.value.push(course.value.sections[0].id);
      const firstModule = getAllModules()[0];
      if (firstModule && (isEnrolled.value || course.value.price === 0)) {
        await selectModule(firstModule, firstModule.sectionId);
      }
    }
  }
  loading.value = false;
});
</script>
<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;
$hover-orange: #cc5800;

.course-view {
  margin-top: 72px;
  min-height: 100vh;
  background: #f8f9fa;
}

.youtube-player {
  iframe {
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: 8px;
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e9ecef;
    border-top: 4px solid $primary-orange;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    margin-top: 16px;
    color: #868e96;
  }
}

/* Course Header */
.course-header {
  background-size: cover;
  background-position: center;
  padding: 48px 24px;
  position: relative;

  .header-content {
    max-width: 1200px;
    margin: 0 auto;
    color: #fff;

    .breadcrumb {
      display: flex;
      gap: 8px;
      font-size: 14px;
      margin-bottom: 16px;

      a {
        color: rgba(255, 255, 255, 0.7);
        text-decoration: none;
        transition: color 0.2s ease;

        &:hover {
          color: #fff;
        }
      }

      .current {
        color: rgba(255, 255, 255, 0.5);
      }
    }

    h1 {
      font-size: 40px;
      font-weight: 800;
      margin: 0 0 12px 0;

      @media (max-width: 768px) {
        font-size: 28px;
      }
    }

    .course-description {
      font-size: 18px;
      opacity: 0.9;
      max-width: 640px;
      margin-bottom: 24px;
      line-height: 1.6;

      @media (max-width: 768px) {
        font-size: 16px;
      }
    }

    .course-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px 24px;
      margin-bottom: 24px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        opacity: 0.85;

        .icon {
          font-size: 18px;
        }
      }
    }

    .course-actions {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;

      .price-tag {
        .price {
          font-size: 32px;
          font-weight: 700;
        }

        .free-badge {
          font-size: 24px;
          font-weight: 700;
          background: rgba(40, 167, 69, 0.2);
          padding: 4px 16px;
          border-radius: 8px;
          border: 2px solid #28a745;
        }
      }

      .btn-enroll {
        padding: 12px 32px;
        background: $primary-orange;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 18px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;

        &:hover:not(:disabled) {
          background: $hover-orange;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255, 128, 0, 0.4);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner-small {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid #fff;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
      }
    }
  }
}

/* Course Body */
.course-body {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;

  .main-content {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 24px;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
    }

    .content-area {
      .player-wrapper {
        background: #1a1a1a;
        overflow: hidden;

        .module-player {
          .video-player {
            position: relative;
            background: #000;

            .video-element {
              width: 100%;
              height: 500px;
              background: #000;
              display: block;
            }

            .video-controls-bottom {
              display: flex;
              justify-content: space-between;
              padding: 12px 16px;
              background: rgba(0, 0, 0, 0.8);
              color: #fff;
              font-size: 14px;
            }
          }

          .article-viewer {
            background: #fff;
            padding: 40px;
            min-height: 400px;

            .article-header {
              margin-bottom: 24px;

              h2 {
                font-size: 28px;
                color: #1a1a1a;
                margin: 0;
              }
            }

            .article-body {
              color: #495057;
              line-height: 1.8;
              font-size: 16px;

              h1,
              h2,
              h3,
              h4,
              h5,
              h6 {
                color: #1a1a1a;
                margin: 16px 0 8px 0;
              }

              p {
                margin: 0 0 12px 0;
              }

              ul,
              ol {
                padding-left: 24px;
                margin: 8px 0;
              }

              blockquote {
                border-left: 4px solid $primary-orange;
                padding-left: 16px;
                margin: 8px 0;
                color: #868e96;
                font-style: italic;
              }

              img {
                max-width: 100%;
                height: auto;
                border-radius: 8px;
                margin: 8px 0;
              }
            }
          }

          .empty-content {
            padding: 60px 24px;
            text-align: center;
            color: #868e96;
            background: #fff;

            svg {
              color: #adb5bd;
              margin-bottom: 16px;
            }

            h3 {
              color: #1a1a1a;
              margin: 0 0 8px 0;
            }

            p {
              margin: 0;
            }
          }
        }

        .module-navigation {
          display: flex;
          justify-content: space-between;
          padding: 12px 16px;
          background: #f8f9fa;
          border-top: 1px solid #e9ecef;

          .nav-btn {
            padding: 8px 16px;
            background: #fff;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-weight: 500;

            &:hover {
              border-color: $primary-orange;
              color: $primary-orange;
            }

            &.prev {
              margin-right: auto;
            }

            &.next {
              margin-left: auto;
            }
          }
        }
      }

      .enrollment-required {
        .enrollment-card {
          background: #fff;
          border-radius: 12px;
          padding: 40px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

          svg {
            color: #adb5bd;
            margin-bottom: 16px;
          }

          h3 {
            font-size: 24px;
            color: #1a1a1a;
            margin: 0 0 8px 0;
          }

          p {
            color: #868e96;
            margin-bottom: 24px;
          }

          .preview-info {
            text-align: left;
            background: #f8f9fa;
            padding: 16px 20px;
            border-radius: 8px;

            h4 {
              margin: 0 0 8px 0;
              color: #1a1a1a;
            }

            ul {
              margin: 0;
              padding-left: 20px;
              color: #495057;

              li {
                margin-bottom: 4px;
              }
            }

            .more-sections {
              margin-top: 8px;
              color: $primary-orange;
              font-weight: 500;
            }
          }
        }
      }
    }

    .course-sidebar {
      display: flex;
      flex-direction: column;
      gap: 16px;

      .sidebar-card {
        background: #fff;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

        h3 {
          font-size: 16px;
          color: #1a1a1a;
          margin: 0 0 16px 0;
        }
      }

      .sections-list {
        .section-group {
          margin-bottom: 12px;

          &:last-child {
            margin-bottom: 0;
          }

          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            background: #f8f9fa;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
              background: #f1f3f5;
            }

            .section-title {
              font-weight: 600;
              font-size: 14px;
              color: #1a1a1a;

              .section-number {
                color: $primary-orange;
              }
            }

            .section-count {
              font-size: 12px;
              color: #868e96;
            }
          }

          .modules-list {
            margin-top: 4px;
            padding-left: 8px;

            .module-item {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 6px 12px;
              border-radius: 6px;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                background: #f8f9fa;
              }

              &.active {
                background: rgba(255, 128, 0, 0.1);
                border-left: 3px solid $primary-orange;
              }

              &.locked {
                opacity: 0.5;
                cursor: not-allowed;
              }

              .module-status {
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                font-size: 12px;
                font-weight: 600;
                flex-shrink: 0;

                .status-number {
                  background: #e9ecef;
                  color: #495057;
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }

                .status-completed {
                  background: #28a745;
                  color: #fff;
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 14px;
                }
              }

              .module-title {
                flex: 1;
                font-size: 13px;
                color: #495057;
              }

              .module-type {
                font-size: 14px;
              }
            }
          }
        }
      }

      .creator-card {
        .creator-info {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;

          .creator-avatar {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            overflow: hidden;
            border: 2px solid $primary-orange;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
          }

          .creator-details {
            h4 {
              margin: 0;
              font-size: 14px;
              color: #1a1a1a;
            }

            p {
              margin: 0;
              font-size: 13px;
              color: #868e96;
            }
          }
        }

        .creator-social {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;

          .social-link {
            padding: 4px 12px;
            background: #f8f9fa;
            border-radius: 6px;
            text-decoration: none;
            font-size: 14px;
            transition: all 0.2s ease;

            &:hover {
              background: #f1f3f5;
              transform: translateY(-2px);
            }
          }
        }
      }

      .stats-card {
        .stats-list {
          .stat-item {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            border-bottom: 1px solid #f1f3f5;

            &:last-child {
              border-bottom: none;
            }

            .stat-label {
              color: #868e96;
              font-size: 14px;
            }

            .stat-value {
              font-weight: 600;
              color: #1a1a1a;
            }
          }
        }
      }
    }
  }

  /* Reviews Section */
  .reviews-section {
    max-width: 1200px;
    margin: 40px auto 0;
    padding-top: 40px;
    border-top: 1px solid #e9ecef;

    .reviews-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h2 {
        font-size: 24px;
        color: #1a1a1a;
        margin: 0;
      }

      .reviews-summary {
        display: flex;
        align-items: center;
        gap: 12px;

        .average-rating {
          font-size: 20px;
          font-weight: 700;
          color: #1a1a1a;
        }

        .review-count {
          font-size: 14px;
          color: #868e96;
        }
      }
    }

    .write-review {
      background: #fff;
      padding: 24px;
      border-radius: 12px;
      margin-bottom: 24px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

      h4 {
        margin: 0 0 12px 0;
        color: #1a1a1a;
      }

      .rating-input {
        display: flex;
        gap: 4px;
        margin-bottom: 12px;

        .star-btn {
          font-size: 28px;
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          opacity: 0.3;

          &.active {
            opacity: 1;
          }

          &:hover {
            transform: scale(1.2);
          }
        }
      }

      textarea {
        width: 100%;
        padding: 12px;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        font-size: 14px;
        font-family: inherit;
        resize: vertical;
        transition: all 0.3s ease;

        &:focus {
          border-color: $primary-orange;
          outline: none;
          box-shadow: 0 0 0 4px rgba(255, 128, 0, 0.1);
        }
      }

      .btn-primary {
        margin-top: 12px;
        padding: 10px 24px;
        background: $primary-orange;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover:not(:disabled) {
          background: $hover-orange;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }

    .reviews-list {
      .no-reviews {
        text-align: center;
        padding: 40px;
        color: #868e96;
        background: #fff;
        border-radius: 12px;
      }

      .review-item {
        background: #fff;
        padding: 20px;
        border-radius: 12px;
        margin-bottom: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

        .review-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;

          .reviewer-info {
            display: flex;
            align-items: center;
            gap: 12px;

            img {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              object-fit: cover;
            }

            .reviewer-name {
              font-weight: 600;
              color: #1a1a1a;
              display: block;
            }

            .review-date {
              font-size: 12px;
              color: #868e96;
            }
          }

          .review-rating {
            font-size: 16px;
          }
        }

        .review-text {
          margin: 0;
          color: #495057;
          line-height: 1.6;
        }
      }
    }
  }

  /* Error State */
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 60vh;
    text-align: center;

    svg {
      color: #adb5bd;
      margin-bottom: 16px;
    }

    h2 {
      color: #1a1a1a;
      margin: 0 0 8px 0;
    }

    p {
      color: #868e96;
      margin-bottom: 24px;
    }

    .btn-primary {
      padding: 12px 32px;
      background: $primary-orange;
      color: #fff;
      text-decoration: none;
      border-radius: 8px;
      font-weight: 600;
      transition: all 0.3s ease;

      &:hover {
        background: $hover-orange;
        transform: translateY(-2px);
      }
    }
  }
}

.module-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 300px;
  background: #fff;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e9ecef;
    border-top: 4px solid #ff8000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  p {
    color: #868e96;
  }
}

.module-item.module-loading {
  opacity: 0.6;
  pointer-events: none;

  .status-loading {
    animation: pulse 1s infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .course-body {
    padding: 16px;

    .main-content {
      .content-area {
        .player-wrapper {
          .module-player {
            .article-viewer {
              padding: 20px;
            }
          }
        }
      }
    }
  }

  .reviews-section {
    padding: 20px 16px 0;

    .reviews-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
  }
}
</style>
